export enum MessageCategory {
  None = 'none',
  SmsAgentReply = 'sms-agent-reply',
  SmsCancellation = 'sms-cancellation',
  SmsCancellationReply = 'sms-cancellation-reply',
  SmsConfirmationReply = 'sms-confirmation-reply',
  SmsConfirmationRequest = 'sms-confirmation-request',
  SmsNoShow = 'sms-no-show',
  SmsReminder = 'sms-reminder',
  SmsUserReply = 'sms-user-reply',
}

export enum MessageHistoryLookupDirection {
  Backward = 'backward',
  Forward = 'forward',
}

export interface FindAllMessagesParams {
  searchString?: string;
  from?: string;
  to?: string;
  direction?: CommunicationDirection;
  messageCategories?: MessageCategory[];
  coreMemberId?: number;
  coreProviderId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  deliveryStatus?: TwilioMessageStatus[];
  resolutionStatus?: MessageResolutionStatus[];
  // Clinic filter (server clamps to the caller's permitted clinics).
  clinicIds?: number[];
  includeUnassigned?: boolean;
}

export type GetMessageHistoryParams =
  | {
      phone: string;
      systemPhone?: string;
      beforeMessageId?: number;
      afterMessageId?: number;
      limit?: number;
    }
  | {
      phone: string;
      systemPhone?: string;
      messageId: number;
      lookupDirection: MessageHistoryLookupDirection;
    };

export interface CreateSmsMessagePayload {
  coreMemberId?: number;
  coreMemberFullname?: string;
  direction?: CommunicationDirection;
  coreProviderId?: number;
  from: string;
  to: string;
  content: string;
  messageCategory: MessageCategory;
}

export enum CommunicationDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export enum MessagePartyType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum TwilioMessageStatus {
  Accepted = 'accepted',
  Scheduled = 'scheduled',
  Canceled = 'canceled',
  Queued = 'queued',
  Sending = 'sending',
  Sent = 'sent',
  Failed = 'failed',
  FailedIntent = 'failed-intent',
  Delivered = 'delivered',
  Undelivered = 'undelivered',
  Receiving = 'receiving',
  Received = 'received',
}

export const TWILIO_ERROR_STATUSES = [
  TwilioMessageStatus.Failed,
  TwilioMessageStatus.FailedIntent,
  TwilioMessageStatus.Undelivered,
  TwilioMessageStatus.Canceled,
];

export enum MessageResolutionStatus {
  Pending = 'PENDING',
  AutomaticallyResolved = 'AUTOMATICALLY_RESOLVED',
  ManuallyResolved = 'MANUALLY_RESOLVED',
}

export interface SimplifiedMessageLog {
  delivery?: {
    status: TwilioMessageStatus;
    updatedAt?: string;
    errorDetails?: {
      code: string;
      message: string;
      description: string;
    };
  };
  resolution?: {
    status: MessageResolutionStatus;
    resolvedAt?: string;
    resolvedByCoreProviderId?: number;
    resolvedByCoreProviderFullname?: string;
  };
  from: {
    phoneNumber: string;
    fromType: MessagePartyType;
    user?: {
      coreMemberId: number | null;
      coreMemberFullname: string | null;
    };
    provider?: {
      coreProviderId: number | null;
      coreProviderFullname: string | null;
    };
  };
  to: {
    phoneNumber: string;
    toType: MessagePartyType;
    user?: {
      coreMemberId: number | null;
      coreMemberFullname: string | null;
    };
    provider?: {
      coreProviderId: number | null;
      coreProviderFullname: string | null;
    };
  };
  direction: CommunicationDirection;
  messageCategory: string;
  content: string;
  createdAt: string;
  id: number;
}

export enum TwilioOptOutType {
  Start = 'START',
  Stop = 'STOP',
  Help = 'HELP',
}

export interface TwilioMessagingWebhookResponse {
  OptOutType?: TwilioOptOutType;
  ToCountry: string;
  ToState: string;
  SmsMessageSid: string;
  NumMedia: string;
  ToCity: string;
  FromZip: string;
  SmsSid: string;
  FromState: string;
  SmsStatus: string;
  FromCity: string;
  Body: string;
  FromCountry: string;
  To: string;
  MessagingServiceSid: string;
  ToZip: string;
  NumSegments: string;
  MessageSid: string;
  AccountSid: string;
  From: string;
  ApiVersion: string;
}

export interface MessageLog {
  id: number;
  coreMemberFullname: string | null;
  coreMemberId: number | null;
  coreProviderFullname: string | null;
  coreProviderId: number | null;
  createdAt: string;
  from: string;
  fromType: MessagePartyType;
  to: string;
  toType: MessagePartyType;
  direction: CommunicationDirection;
  content: string;
  messageCategory: string;
  distanceInHoursToStart: number | null;
  metadata: TwilioMessagingWebhookResponse | null;
  communicationTemplateId: number | null;
  communicationTriggerId: number | null;
  deliveryStatus: TwilioMessageStatus;
  resolutionStatus: MessageResolutionStatus;
  resolvedAt: string | null;
  resolvedByCoreProviderFullname: string | null;
  resolvedByCoreProviderId: number | null;
}

export interface ChatsByPhoneAndDate {
  [phone: string]: { [date: string]: MessageLog[] };
}

export interface SimplifiedMemberCommunicationStatus {
  emailOptedOut: boolean;
  smsOptedOut: boolean;
}

export interface MessageLogsBySystemPhoneAndDate {
  memberCommunicationStatus: SimplifiedMemberCommunicationStatus;
  chats: ChatsByPhoneAndDate;
}

export enum AppointmentTimeTag {
  Upcoming1h = '1',
  Upcoming24h = '24',
  Upcoming48h = '48',
  Upcoming72h = '72',
}

export enum AppointmentLanguage {
  EN = 'EN',
  ES = 'ES',
  AR = 'AR',
}

export interface FindAllAppointmentsParams {
  coreMemberId?: string;
  coreProviderId?: string;
  confirmationStatus?: boolean;
  timeTag?: AppointmentTimeTag;
  appointmentLanguage?: AppointmentLanguage;
  startAtFrom?: string;
  startAtTo?: string;
  page?: number;
  pageSize?: number;
  // Clinic filter (server clamps to the caller's permitted clinics).
  clinicIds?: number[];
  includeUnassigned?: boolean;
}

export interface FindAllMembersParams {
  coreMemberId?: string;
  optedOutMethods?: string[];
  unreachableStatus?: string;
  page?: number;
  pageSize?: number;
  // Clinic filter (server clamps to the caller's permitted clinics).
  clinicIds?: number[];
  includeUnassigned?: boolean;
}

export interface UpdateAppointmentConfirmationStatusPayload {
  confirmationStatus: boolean;
  coreAppointmentId: number;
}

export interface UpdateMessageResolutionStatusPayload {
  resolutionStatus: MessageResolutionStatus;
  resolutionByCoreProviderId: number;
  resolutionByCoreProviderFullname: string;
}
export interface SimplifiedAppointment {
  coreAppointmentId: number;
  coreMemberFullname: string;
  coreMemberId: number;
  coreProviderFullname: string;
  coreProviderId: number;
  appointmentStartAt: string;
  appointmentEndAt: string;
  coreStatus: string;
  confirmed: boolean;
  coreNoShow: boolean;
  appointmentTypeCode: string | null;
  timeTag: AppointmentTimeTag;
}

export interface UpdateMemberOptedOutPayload {
  coreMemberId: number;
  smsOptedOutStatus?: boolean;
  emailOptedOutStatus?: boolean;
}

export interface UpdateMemberUnreachablePayload {
  coreMemberId: number;
  isUnreachable: boolean;
  unreachableReason?: string;
}

/** Provenance of a member's clinic of record (mirrors the appointments-api enum). */
export type ClinicSource = 'CORE' | 'APPOINTMENT' | 'MANUAL';

export type MemberCommunicationStatus = {
  coreMemberId: number;
  id: number;
  lastTrackingNumberId: number | null;
  smsOptedOut: boolean;
  smsOptedOutAt: Date | null;
  emailOptedOut: boolean;
  emailOptedOutAt: Date | null;
  isUnreachable: boolean;
  unreachableAt: Date | null;
  unreachableReason: string | null;
  // Denormalized clinic of record + provenance (drives comms clinic-scoping).
  coreMemberClinicId: number | null;
  coreMemberClinicName: string | null;
  clinicSource: ClinicSource | null;
  clinicSyncedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

/** Scope for a background clinic-sync sweep (POST /communications/clinic-sync). */
export type ClinicSyncScope = 'unassigned' | 'drifted' | 'all' | 'memberIds';

export interface UpdateMemberClinicPayload {
  coreMemberId: number;
  /** `fromCore` pulls the member's current Core clinic; `manual` is an operator override. */
  mode: 'fromCore' | 'manual';
  clinicId?: number;
  clinicName?: string;
}

export interface TriggerClinicSyncPayload {
  scope: ClinicSyncScope;
  memberIds?: number[];
}

/** Result of a single-member clinic repair (PUT .../clinic). */
export interface MemberClinicReconcileResult {
  coreMemberId: number;
  clinic: { coreMemberClinicId: number | null; coreMemberClinicName: string | null };
  source: ClinicSource | null;
  skipped: boolean;
  affectedCounts: {
    messages: number;
    calls: number;
    voicemails: number;
    conferences: number;
    conferenceParticipants: number;
  };
}

export interface ClinicSyncJobResponse {
  jobId: string;
}

export type MemberWithCommunicationStatus = MemberCommunicationStatus & {
  member: {
    clinic: {
      id: string | null;
      name: string | null;
    };
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
};

export enum TrackingNumberStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type TrackingNumber = {
  trackingNumber: string;
  id: number;
  status: TrackingNumberStatus;
  enabledForReminders: boolean;
  label: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export enum AgentTrackingNumberUsageCategory {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  SMS = 'SMS',
}

export type TrackingNumberWithAgents = TrackingNumber & {
  agents?: {
    id: number;
    coreProviderId: number;
    usageCategory: AgentTrackingNumberUsageCategory;
    priority: number;
    isPrimary: boolean;
  }[];
};

export type TrackingNumberResponse = {
  availableNumbers: TrackingNumberWithAgents[];
};

export type MessageLogWithLastOutboundMessage = MessageLog & {
  lastOutboundMessageSent?: MessageLog | null;
  trackingNumber?: TrackingNumber | null;
};

export type MessageLogsByDateAndLastOutboundResponse = {
  [date: string]: MessageLogWithLastOutboundMessage[];
};

export type ExportMessagesPayload = {
  searchString?: string;
  coreMemberId?: number;
  coreProviderId?: number;
  from?: string;
  to?: string;
  direction?: CommunicationDirection;
  messageCategories?: MessageCategory[];
  startDate?: string;
  endDate?: string;
  deliveryStatus?: TwilioMessageStatus[];
  resolutionStatus?: MessageResolutionStatus[];
  orderBy?: 'asc' | 'desc';
};
